/* Copyright (c) 2024 MOCA Systems, Inc.  All rights reserved. */

import { BehaviorSubject } from 'rxjs';
import { Ticket } from '@touchplan/common';
import { CommonModule } from '@angular/common';
import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
	ViewChild,
} from '@angular/core';

import * as appStrings from '@touchplan/strings';
import { TicketListItemComponent } from '../ticket-list-item/ticket-list-item.component';
import { MultiPanelModule } from '../../multi-panel/multi-panel.module';

const MODULES = [CommonModule, ScrollingModule, MultiPanelModule];
const DEFAULT_TICKET_HEIGHT_PX = 100;

//...adding panel to the name since ticketList is so ungodly overused
@Component({
	selector: 'tp-ticket-list-panel',
	standalone: true,
	templateUrl: './ticket-list-panel.component.html',
	styleUrls: ['./ticket-list-panel.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [...MODULES, TicketListItemComponent],
})
export class TicketListPanelComponent implements OnDestroy, OnInit {
	@ViewChild(CdkVirtualScrollViewport) ticketsViewport!: CdkVirtualScrollViewport;
	@Input() set tickets(tickets: Map<string, Ticket>) {
		const ticketArray = Array.from(tickets.values());
		this.tickets$.next(ticketArray);

		// Determine which ticket (if any) should be focused
		const cycleSelectedIndex = ticketArray.findIndex((ticket) => ticket.cycleSelected);
		const selectedIndex = ticketArray.findIndex((ticket) => ticket.selected);

		// Priority: cycleSelected > selected > none
		const focusIndex = cycleSelectedIndex >= 0 ? cycleSelectedIndex : selectedIndex >= 0 ? selectedIndex : -1;

		this.focusedTicketIndex$.next(focusIndex);
	}

	@Output() public ticketClick: EventEmitter<Ticket> = new EventEmitter<Ticket>();

	protected tickets$: BehaviorSubject<Ticket[]> = new BehaviorSubject<Ticket[]>([]);
	protected focusedTicketIndex$: BehaviorSubject<number> = new BehaviorSubject<number>(-1);
	protected appStrings = appStrings;
	protected defaultTicketHeight = DEFAULT_TICKET_HEIGHT_PX;

	public onTicketClick(ticket: Ticket): void {
		this.ticketClick.emit(ticket);
	}

	public ngOnInit(): void {
		this.scrollToFocusedTicket();
	}

	private scrollToFocusedTicket(): void {
		this.focusedTicketIndex$.subscribe((index) => {
			if (this.ticketsViewport && index >= 0) {
				this.ticketsViewport.scrollToIndex(index, 'smooth');
			}
		});
	}

	public ngOnDestroy(): void {
		this.tickets$.complete();
		this.focusedTicketIndex$.complete();
	}
}
